import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActionItem } from '../../shared/components/dropdown/dropdown.component';
import { DropdownComponent } from '../../shared/components/dropdown/dropdown.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { NoDataComponent } from '../../shared/components/no-data/no-data.component';
import { AddAttorneyComponent } from './add-attorney/add-attorney.component';
import { AttorneyService, Attorney } from './services/attorney.service';
import { SnackbarService } from '../../core/services/snackbar.service';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { Search01Icon, Add01Icon, PencilEdit01Icon, ArrowLeft01Icon, UserBlock01Icon, CheckmarkCircle02Icon, Cancel01Icon } from '@hugeicons/core-free-icons';
import { DynamicHeightDirective } from '../../shared/directives/dynamic-height.directive';

@Component({
    selector: 'app-attorney',
    standalone: true,
    imports: [FormsModule, PaginationComponent, AddAttorneyComponent, NoDataComponent, DropdownComponent, HugeiconsIconComponent, DynamicHeightDirective],
    templateUrl: './attorney.component.html',
    styleUrl: './attorney.component.scss'
})
export class AttorneyComponent implements OnInit {
    currentPage = 1;
    itemsPerPage = 20;
    totalItems = 0;
    searchTerm: string = '';

    // Icons
    searchIcon = Search01Icon;
    addIcon = Add01Icon;
    arrowLeftIcon = ArrowLeft01Icon;
    noDataIcon = UserBlock01Icon;

    allAttorneys: Attorney[] = []; // Store all data
    attorneys: Attorney[] = []; // Store displayed data
    isAdding = false;
    selectedAttorney: Attorney | null = null;

    actionsMap = new Map<number, ActionItem[]>();

    avatarColors = [
        { bg: '#fee2e2', color: '#dc2626' }, // Red
        { bg: '#dbeafe', color: '#2563eb' }, // Blue
        { bg: '#dcfce7', color: '#16a34a' }, // Green
        { bg: '#fef3c7', color: '#d97706' }, // Amber
        { bg: '#ede9fe', color: '#7c3aed' }, // Violet
        { bg: '#fce7f3', color: '#db2777' }, // Pink
        { bg: '#ccfbf1', color: '#0d9488' }, // Teal
        { bg: '#ffedd5', color: '#ea580c' }, // Orange
    ];

    getAvatarColor(id: number | undefined) {
        return this.avatarColors[(id || 0) % this.avatarColors.length];
    }

    constructor(
        private attorneyService: AttorneyService,
        private snackbar: SnackbarService
    ) { }

    ngOnInit(): void {
        this.loadAttorneys();
    }

    handleTableAction(action: string, attorney: Attorney) {
        switch (action) {
            case 'edit':
                this.selectedAttorney = attorney;
                this.isAdding = true;
                break;
            case 'toggle-status':
                this.toggleStatus(attorney);
                break;
        }
    }

    loadAttorneys() {
        this.attorneyService.getAttorneys().subscribe({
            next: (data) => {
                this.allAttorneys = data;
                this.applySearchAndPagination();
            },
            error: (err) => {
                this.snackbar.show('Failed to load attorneys', 'error');
            }
        });
    }

    onSearch() {
        this.currentPage = 1;
        this.applySearchAndPagination();
    }

    applySearchAndPagination() {
        // First filter by search term
        const term = this.searchTerm.toLowerCase().trim();
        const filtered = this.allAttorneys.filter(a => {
            if (!term) return true;
            return a.attorney_name?.toLowerCase().includes(term) ||
                a.firm_name?.toLowerCase().includes(term) ||
                a.attorney_email?.toLowerCase().includes(term) ||
                a.id?.toString().includes(term);
        });

        this.totalItems = filtered.length;

        // Then apply pagination
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = startIndex + this.itemsPerPage;
        this.attorneys = filtered.slice(startIndex, endIndex);
        this.buildActionsMap();
    }

    private buildActionsMap(): void {
        this.actionsMap.clear();
        for (const attorney of this.attorneys) {
            if (!attorney.id) continue;
            const isActive = attorney.user?.status === 'ACTIVE';
            this.actionsMap.set(attorney.id, [
                { label: 'Edit', icon: PencilEdit01Icon, action: 'edit' },
                isActive
                    ? { label: 'Inactive', icon: Cancel01Icon, action: 'toggle-status', variant: 'danger' as const }
                    : { label: 'Activate', icon: CheckmarkCircle02Icon, action: 'toggle-status' },
            ]);
        }
    }

    updateDisplayedAttorneys() {
        this.applySearchAndPagination();
    }

    toggleAdd() {
        this.isAdding = !this.isAdding;
        if (!this.isAdding) {
            this.selectedAttorney = null;
        }
    }

    handleSaveAttorney(attorneyData: any) {
        const formData = new FormData();

        Object.keys(attorneyData).forEach(key => {
            const value = attorneyData[key];
            if (value !== null && value !== undefined) {
                if (key === 'profile_image') {
                    if (value instanceof File) {
                        formData.append('profile_image', value);
                    }
                } else {
                    formData.append(key, value);
                }
            }
        });

        if (this.selectedAttorney && this.selectedAttorney.id) {
            this.attorneyService.updateAttorney(this.selectedAttorney.id, formData).subscribe({
                next: (updatedAttorney) => {
                    this.snackbar.show('Attorney updated successfully', 'success');
                    this.loadAttorneys();
                    this.isAdding = false;
                    this.selectedAttorney = null;
                },
                error: (err) => {
                    this.snackbar.show('Failed to update attorney', 'error');
                }
            });
        } else {
            formData.append('status', 'SUSPENDED');
            this.attorneyService.addAttorney(formData).subscribe({
                next: (savedAttorney) => {
                    this.snackbar.show('Attorney added successfully', 'success');
                    this.loadAttorneys();
                    this.isAdding = false;
                },
                error: (err) => {
                    this.snackbar.show('Failed to add attorney', 'error');
                }
            });
        }
    }

    toggleStatus(attorney: Attorney) {
        if (!attorney.id) return;
        const newStatus = attorney.user?.status === 'ACTIVE' ? 'deactive' : 'active';
        this.attorneyService.toggleStatus(attorney.id, newStatus).subscribe({
            next: () => {
                this.snackbar.show(`Attorney ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`, 'success');
                this.loadAttorneys();
            },
            error: () => {
                this.snackbar.show('Failed to update status', 'error');
            }
        });
    }

    onPageChange(page: number) {
        this.currentPage = page;
        this.applySearchAndPagination();
    }
}
